import { ProfitLossPage } from "./pages/ProfitLossPage";
import { KassaPage } from "./pages/KassaPage";
import { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast, Toaster } from "sonner";
import { api, ARCHIVED_FLAG, getToken, setToken, type UserOut } from "./api";
import { PageHeaderProvider } from "./lib/use-page-header";
import { TariffProvider, useTariff } from "./lib/use-tariff";
import { hiddenTabs } from "./lib/plan-visibility";
import { BranchProvider } from "./lib/use-branch";
import { ThemeProvider, useTheme } from "./lib/use-theme";
import { useSidebarCollapse } from "./lib/use-sidebar";
import { Login } from "./pages/Login";
import { ChangePassword } from "./pages/ChangePassword";
import { FeatureGate } from "./components/feature-gate";
import { Sidebar } from "./components/sidebar";
import { Topbar } from "./components/topbar";
import { Skeleton } from "./components/ui/skeleton";
import { Sheet } from "./components/ui/sheet";
import { HomePage } from "./pages/HomePage";
import { WorksPage } from "./pages/WorksPage";
import { OmborPage, type OmborJump } from "./pages/OmborPage";
import { QarzlarPage } from "./pages/QarzlarPage";
import { ClientsPage } from "./pages/ClientsPage";
import { BroadcastPage } from "./pages/BroadcastPage";
import { EmployeesPage } from "./pages/EmployeesPage";
import { RemindersPage } from "./pages/RemindersPage";
import { AnalyticsPage } from "./pages/AnalyticsPage";
import { MurojaatlarPage } from "./pages/MurojaatlarPage";
import { SettingsPage } from "./pages/SettingsPage";
import { FinancePage } from "./pages/FinancePage";
import { useFinance } from "./lib/use-finance";
import {
  pathForRoute,
  readDashboardRoute,
  validateDashboardRoute,
  writeDashboardRoute,
  type DashboardRoute,
  type Tab,
} from "./routing";

function ThemedToaster() {
  const { theme } = useTheme();
  return (
    <Toaster
      theme={theme}
      position="top-right"
      richColors
      closeButton
      duration={3500}
    />
  );
}

function ExpiredBanner() {
  const { t } = useTranslation("common");
  return (
    <div className="sticky top-0 z-[60] w-full bg-[#F5A623] text-[#1A1A1A] px-4 py-2.5 text-center text-sm font-semibold shadow-md">
      {t("subscription.expiredBanner")}
    </div>
  );
}

function Shell({ user, onLogout }: { user: UserOut; onLogout: () => void }) {
  const { t } = useTranslation("common");
  const [route, setRoute] = useState<DashboardRoute>(
    () => readDashboardRoute() ?? { tab: "home" },
  );
  const tab = route.tab;
  const [menuOpen, setMenuOpen] = useState(false);
  const menuTriggerRef = useRef<HTMLButtonElement>(null);
  // Desktop sidebar ikonka-rail rejimi (localStorage'da saqlanadi).
  const { collapsed, toggle: toggleSidebar } = useSidebarCollapse();
  // Ombordagi tovar harakatlari jurnalidan "ishga biriktirilgan tovar"
  // satriga bosilsa — Ishlar sahifasiga o'tiladi. `seq` bir xil ish qayta
  // bosilganda ham qayta ochilishi uchun.
  const [focusWork, setFocusWork] = useState<{ id: number; seq: number } | null>(
    null,
  );
  // Tahlil > Signallar qatoridagi amal Ombor ichiga tushadi (xarid formasi /
  // hisobdan chiqarish / xarid hujjati). Ombor sahifasi Tahlil ochiq turganda
  // unmount bo'lgani uchun payload mount'da bir marta iste'mol qilinadi —
  // `focusWork` dagi kabi seq kerak emas.
  const [omborJump, setOmborJump] = useState<OmborJump | null>(null);
  const { status } = useTariff();
  const expired = !!(status?.is_expired || status?.status === "expired");
  const finance = useFinance();
  const hidden = useMemo(() => {
    const result = new Set(hiddenTabs(status?.features));
    if (finance.status && !finance.active("kassa")) result.add("kassa");
    if (finance.status && !finance.active("foyda_zarar")) result.add("foyda_zarar");
    if (finance.status && !finance.active("ish_haqi")) result.add("ish_haqi");
    if (finance.status && !finance.active("xarajatlar")) result.add("xarajatlar");
    return result;
  }, [status?.features, finance.status]);

  // To'g'ridan-to'g'ri kirishni ham to'sish: tarifda yo'q bo'lim so'ralsa — bosh
  // sahifaga qaytariladi (sidebar'da bunday tugma yo'q, lekin HomePage'dagi
  // "Qarz daftar" CTA singari ichki yo'llar bor).
  const applyRoute = (requested: DashboardRoute, push = true) => {
    const nextRoute = validateDashboardRoute(requested, hidden);
    const next = nextRoute.tab;
    // Ishlar sahifasi tabdan chiqilganda unmount bo'ladi; fokus tozalanmasa
    // keyingi safar ochilganda eski ish yana ochilib, eski kesim yana
    // qo'llanardi. `onOpenWork` fokusni SHU chaqiruvdan keyin o'rnatadi.
    if (next !== "works") setFocusWork(null);
    if (next !== "tovars" && next !== "qaytarishlar") setOmborJump(null);
    setRoute(nextRoute);
    if (push) writeDashboardRoute(nextRoute);
    setMenuOpen(false);
  };

  const handleSetTab = (t: Tab) => applyRoute({ tab: t });

  // Back/Forward restores both the visible page and History state payloads.
  useEffect(() => {
    const onPopState = () => applyRoute(readDashboardRoute() ?? { tab: "home" }, false);
    window.addEventListener("popstate", onPopState);
    return () => window.removeEventListener("popstate", onPopState);
  });

  // Unknown initial URLs are replaced, rather than leaving a dead entry behind.
  useEffect(() => {
    if (!readDashboardRoute()) writeDashboardRoute({ tab: "home" }, true);
  }, []);

  // TariffProvider asinxron yuklanadi — tab allaqachon tanlangan bo'lishi mumkin,
  // shuning uchun tarif kelgach yana bir marta tekshiramiz.
  useEffect(() => {
    if (hidden.has(tab)) {
      const home = { tab: "home" as const };
      setRoute(home);
      writeDashboardRoute(home, true);
    }
  }, [hidden, tab]);

  return (
    <div className="min-h-screen flex flex-col bg-background">
      {expired && <ExpiredBanner />}
      <div className="flex flex-1 min-h-0">
      {/* Desktop sidebar */}
      <div className="hidden lg:flex flex-shrink-0 sticky top-0 h-screen">
        <Sidebar
          tab={tab}
          setTab={handleSetTab}
          user={user}
          onLogout={onLogout}
          collapsed={collapsed}
        />
      </div>

      {/* Mobile sidebar (sheet) */}
      <Sheet
        open={menuOpen}
        onOpenChange={setMenuOpen}
        label={t("nav.mobileDrawer")}
        finalFocusRef={menuTriggerRef}
      >
            <Sidebar
              tab={tab}
              setTab={handleSetTab}
              user={user}
              onLogout={onLogout}
              onClose={() => setMenuOpen(false)}
            />
      </Sheet>

      <PageHeaderProvider>
        <div className="flex-1 min-w-0 flex flex-col">
          <Topbar
            tab={tab}
            onMenu={() => setMenuOpen(true)}
            menuTriggerRef={menuTriggerRef}
            sidebarCollapsed={collapsed}
            onToggleSidebar={toggleSidebar}
          />
          <main className="dashboard-main flex-1 p-4 sm:p-6 lg:p-8 w-full min-w-0">
            {tab === "foyda_zarar" && <ProfitLossPage />}
            {tab === "kassa" && <KassaPage />}
            {(tab === "ish_haqi" || tab === "xarajatlar") && <FinancePage key={tab} module={tab} />}
            {tab === "home" && (
              <HomePage
                onOpenWorks={() => handleSetTab("works")}
                onOpenDebts={() => handleSetTab("qarzlar")}
              />
            )}
            {tab === "works" && (
              <WorksPage
                onOpenDebts={() => handleSetTab("qarzlar")}
                focusWork={route.focusWorkId ? { id: route.focusWorkId, seq: 1 } : focusWork}
              />
            )}
            {/* Ombor — Tovarlar/Xaridlar/Sotuvlar/Qaytarishlar/Jarayonda tablari.
                `qaytarishlar` endi menyuda yo'q (Ombor ichiga ko'chdi), lekin
                eski kirish nuqtasi kelib qolsa to'g'ri tabga tushsin. key bilan
                remount qilamiz, shunda boshlang'ich tab tanlanadi. */}
            {(tab === "tovars" || tab === "qaytarishlar") && (
              <OmborPage
                key={pathForRoute(route)}
                initialTab={route.omborSection ?? (tab === "qaytarishlar" ? "qaytarishlar" : "tovarlar")}
                jump={route.omborJump ?? omborJump}
                onTabChange={(omborSection) => applyRoute({ tab: omborSection === "qaytarishlar" ? "qaytarishlar" : "tovars", omborSection })}
                onOpenTariff={() => handleSetTab("tariff")}
                onOpenWork={(workId) => {
                  setFocusWork((prev) => ({ id: workId, seq: (prev?.seq ?? 0) + 1 }));
                  applyRoute({ tab: "works", focusWorkId: workId });
                }}
              />
            )}
            {/* Sahifaning o'zi ham gate ostida: sidebar'da band butunlay
                yashirin, ammo ichki yo'llar (HomePage CTA) kelib qolsa gate
                ushlab qoladi. */}
            {tab === "qarzlar" && (
              <FeatureGate feature="qarzlar" requiredTariff="comfort">
                <QarzlarPage />
              </FeatureGate>
            )}
            {tab === "clients" && <ClientsPage />}
            {/* Broadcast'ning o'z bayrog'i bor (transactional'dan alohida) —
                sidebar'da band yashirin bo'lsa ham ichki yo'l kelib qolsa gate
                ushlab qoladi (qarzlar bilan bir xil naqsh). */}
            {tab === "broadcast" && (
              <FeatureGate feature="broadcast" requiredTariff="comfort">
                <BroadcastPage />
              </FeatureGate>
            )}
            {tab === "employees" && (
              <EmployeesPage onOpenTariff={() => handleSetTab("tariff")} />
            )}
            {tab === "reminders" && <RemindersPage />}
            {tab === "murojaatlar" && (
              <MurojaatlarPage onOpenTariff={() => handleSetTab("tariff")} />
            )}
            {tab === "analytics" && (
              <AnalyticsPage
                onOpenReminders={() => handleSetTab("reminders")}
                onOpenTariff={() => handleSetTab("tariff")}
                onOmborJump={(jump) => {
                  // Avval payload, keyin tab: `handleSetTab` faqat Ombor'dan
                  // BOSHQA yo'nalishda tozalaydi, shuning uchun shu tartibda
                  // payload yo'qolmaydi.
                  setOmborJump(jump);
                  applyRoute({ tab: "tovars", omborSection: jump.kind === "chiqim" ? "chiqim" : "xaridlar", omborJump: jump });
                }}
              />
            )}
            {/* Sozlamalar — Xizmatlar/Filiallar/Tarif sub-tablar bilan.
                tariff tab upsell tugmalaridan keladi → Tarif sub-tabga ochiladi.
                key bilan remount qilamiz, shunda sub-tab to'g'ri tanlanadi. */}
            {(tab === "settings" || tab === "tariff") && (
              <SettingsPage
                key={pathForRoute(route)}
                initialSection={route.settingsSection ?? (tab === "tariff" ? "tarif" : "umumiy")}
                onSectionChange={(settingsSection) => applyRoute({ tab: settingsSection === "tarif" ? "tariff" : "settings", settingsSection })}
              />
            )}
          </main>
        </div>
      </PageHeaderProvider>
      </div>
    </div>
  );
}

function ArchivedScreen() {
  return (
    <div className="min-h-screen flex items-center justify-center p-6 bg-background">
      <div className="max-w-md w-full text-center space-y-4">
        <div className="text-5xl">⚠️</div>
        <h1 className="text-xl font-semibold text-foreground">
          Ustaxona arxivlangan
        </h1>
        <p className="text-muted-foreground">
          Ustaxona vaqtincha to'xtatilgan. Iltimos, administrator bilan bog'laning.
        </p>
        <button
          className="mt-2 inline-flex h-10 items-center justify-center rounded-md bg-primary px-6 text-primary-foreground font-medium"
          onClick={() => {
            sessionStorage.removeItem(ARCHIVED_FLAG);
            window.location.reload();
          }}
        >
          Chiqish
        </button>
      </div>
    </div>
  );
}

export function App() {
  const { t } = useTranslation("common");
  const logoutPending = useRef(false);
  const [user, setUser] = useState<UserOut | null>(null);
  const [loading, setLoading] = useState(true);
  const [archived] = useState(() => sessionStorage.getItem(ARCHIVED_FLAG) === "1");

  async function logout() {
    if (logoutPending.current) return;
    logoutPending.current = true;
    try {
      await api.logout();
      setToken(null);
      setUser(null);
    } catch {
      toast.error(t("logoutFailed"));
    } finally {
      logoutPending.current = false;
    }
  }

  useEffect(() => {
    (async () => {
      if (archived) {
        setLoading(false);
        return;
      }
      if (!getToken()) {
        setLoading(false);
        return;
      }
      try {
        const me = await api.me();
        if (me.role !== "workshop_owner") {
          setToken(null);
          setLoading(false);
          return;
        }
        setUser(me);
      } catch {
        setToken(null);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  return (
    <ThemeProvider>
      <ThemedToaster />
      {loading ? (
        <div className="min-h-screen flex items-center justify-center p-6">
          <Skeleton className="h-20 w-64" />
        </div>
      ) : archived ? (
        <ArchivedScreen />
      ) : !user ? (
        <Login onLogin={setUser} />
      ) : user.must_change_password ? (
        // Admin/bot parolni qayta tikladi → majburiy almashtirish. Shu hal
        // qilinmaguncha Shell/providerlarni mount QILMAYMIZ: ular himoyalangan
        // endpointlarga 401 (password_change_required) bilan urilib, login'ga
        // qaytaruvchi reload tsiklini keltirib chiqaradi.
        <ChangePassword onDone={setUser} onLogout={logout} />
      ) : (
        <TariffProvider>
          <BranchProvider>
            <Shell
              user={user}
              onLogout={logout}
            />
          </BranchProvider>
        </TariffProvider>
      )}
    </ThemeProvider>
  );
}
